import {Box, Button, InputAdornment, MenuItem, Stack, TextField, Typography} from "@mui/material";
import {useState} from "react";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowRight01Icon, Building01Icon, CircleCheckIcon, Mail01Icon, PhoneIcon, UserIcon} from "@hugeicons/core-free-icons";
import {c, maxW, section, primaryButtonSx, softShadow} from "../landingTokens";
import {MailApiRepository} from "../../../repositories/MailApiRepository";

const CALL_VOLUMES = ["Under 5,000", "5,000 – 25,000", "25,000 – 100,000", "Over 100,000"];

const CONTACT_EMAIL = "contact@ireason.mk";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "rgba(124,108,246,0.05)",
    borderRadius: "12px",
    fontSize: "14.5px",
    transition: "background-color 300ms cubic-bezier(.16,1,.3,1), box-shadow 300ms cubic-bezier(.16,1,.3,1)",
    "& fieldset": {borderColor: "rgba(124,108,246,0.22)", transition: "border-color 250ms ease"},
    "&:hover fieldset": {borderColor: "rgba(124,108,246,0.4)"},
    "&.Mui-focused": {bgcolor: c.white, boxShadow: "0 0 0 4px rgba(98,88,232,0.12)"},
    "&.Mui-focused fieldset": {borderColor: c.purpleBrand, borderWidth: 1.5},
    "&.Mui-error fieldset": {borderColor: "#EF4444"},
    "&.Mui-error.Mui-focused": {boxShadow: "0 0 0 4px rgba(239,68,68,0.12)"},
  },
  "& .MuiInputLabel-root": {fontSize: "13px", color: c.inkSoft},
  "& .MuiInputLabel-root.Mui-focused": {color: c.purpleBrand},
  "& .MuiInputLabel-root.Mui-error": {color: "#EF4444"},
  "& .MuiFormHelperText-root": {fontSize: "11.5px", marginLeft: "4px", marginTop: "5px"},
};

// Contact links live on the purple panel. The landing's global `a` / `a:hover` rules (which set
// color:inherit and color:var(--accent)) out-specify a plain sx color, so we use `&&` to keep the
// links white at rest and on hover, adding only a subtle underline on hover.
const contactLinkSx = {
  fontSize: "14.5px",
  transition: "text-decoration-color 200ms ease",
  "&&": {color: "#FFFFFF", textDecoration: "none"},
  "&&:hover": {color: "#FFFFFF", textDecoration: "underline", textUnderlineOffset: "3px", textDecorationColor: "rgba(255,255,255,0.85)"},
};

const adorn = (Icon) => ({
  startAdornment: (
    <InputAdornment position="start">
      <HugeiconsIcon icon={Icon} size={17} strokeWidth={1.7} color={c.inkMuted} />
    </InputAdornment>
  ),
});

const EMPTY = {name: "", phone: "", email: "", company: "", volume: CALL_VOLUMES[1], message: ""};

const getErrors = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = "Please enter your name";
  if (!f.phone.trim()) e.phone = "Add a phone number we can reach you on";
  if (!f.email.trim()) e.email = "Add your work email";
  else if (!EMAIL_RE.test(f.email)) e.email = "That email doesn't look right";
  if (!f.message.trim()) e.message = "Tell us what you'd automate first";
  return e;
};

export const ContactDemo = () => {
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const errors = getErrors(form);
  const showErr = (field) => (touched[field] || submitted) && Boolean(errors[field]);

  const set = (field) => (e) => setForm((prev) => ({...prev, [field]: e.target.value}));
  const markTouched = (field) => () => setTouched((prev) => ({...prev, [field]: true}));

  const fieldProps = (field, InputPropsExtra) => ({
    value: form[field],
    onChange: set(field),
    onBlur: markTouched(field),
    error: showErr(field),
    helperText: showErr(field) ? errors[field] : " ",
    sx: fieldSx,
    ...(InputPropsExtra ? {InputProps: InputPropsExtra} : {}),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length) return;

    setStatus("sending");
    try {
      await MailApiRepository.contactUs(form);
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  const reset = () => {
    setForm(EMPTY);
    setTouched({});
    setSubmitted(false);
    setStatus("idle");
  };

  return (
    <Box component="section" id="demo" data-screen-label="Contact" sx={{px: section.px, pb: section.pb}}>
      <Box
        className="reveal-stagger"
        sx={{
          maxWidth: maxW,
          mx: "auto",
          borderRadius: "34px",
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"},
          bgcolor: c.white,
          boxShadow: softShadow,
        }}
      >
        {/* Left: the pitch */}
        <Box
          sx={{
            background: "linear-gradient(150deg, #6258E8 0%, #9A6EF5 55%, #C87CF0 100%)",
            p: "clamp(32px, 4vw, 56px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Soft lavender bloom top-left + a lighter pool bottom-right for depth without washing out text. */}
          <Box aria-hidden sx={{position: "absolute", top: -140, left: -110, width: 340, height: 340, borderRadius: "50%", background: `radial-gradient(circle, ${c.purpleSoft}, transparent 70%)`, opacity: 0.5}} />
          <Box aria-hidden sx={{position: "absolute", bottom: -120, right: -90, width: 320, height: 320, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.12)"}} />
          <Box sx={{position: "relative"}}>
            <Typography component="h2" sx={{fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1.06, letterSpacing: "-0.03em", fontWeight: 600, color: c.white, mb: 1.75, textWrap: "balance"}}>
              See it answer a real call.
            </Typography>
            <Typography sx={{fontSize: 17, lineHeight: 1.5, color: "rgba(255,255,255,0.88)", mb: 4.25}}>
              Book a 30-minute demo. We'll run your own call scenarios through the agent, live.
            </Typography>

            <Stack gap={1.75}>
              {["30-minute call, no slide deck", "Your scripts and your language", "Pilot scoping in the same session"].map((line) => (
                <Stack key={line} direction="row" alignItems="center" gap={1.25}>
                  <HugeiconsIcon icon={CircleCheckIcon} size={18} color={c.white} style={{flexShrink: 0}} />
                  <Typography sx={{fontSize: "14.5px", color: c.white}}>{line}</Typography>
                </Stack>
              ))}
            </Stack>

            <Stack gap={1} sx={{mt: 5.5, pt: 3.25, borderTop: "1px solid rgba(255,255,255,0.28)"}}>
              <Typography component="a" href="tel:+38975753932" sx={contactLinkSx}>
                +389 75 753 932
              </Typography>
              <Typography component="a" href={`mailto:${CONTACT_EMAIL}`} sx={contactLinkSx}>
                {CONTACT_EMAIL}
              </Typography>
              <Typography sx={{fontSize: "14.5px", color: "rgba(255,255,255,0.8)"}}>
                3rd Macedonian Brigade 37, Skopje
              </Typography>
            </Stack>
          </Box>
        </Box>

        {/* Right: the form (or its success state) */}
        {status === "sent" ? (
          <Stack alignItems="center" justifyContent="center" sx={{p: "clamp(32px, 4vw, 56px)", textAlign: "center", gap: 2.25, minHeight: {xs: 360, md: "100%"}}}>
            <Box sx={{width: 66, height: 66, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "rgba(16,185,129,0.12)", color: "#10B981"}}>
              <HugeiconsIcon icon={CircleCheckIcon} size={34} />
            </Box>
            <Typography sx={{fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em", color: c.ink}}>
              Successfully sent!
            </Typography>
            <Typography sx={{fontSize: 15, lineHeight: 1.55, color: c.inkSoft, maxWidth: 360}}>
              We've got your message — thanks for reaching out! We'll get back to you within one business day.
            </Typography>
            <Button onClick={reset} sx={{mt: 0.5, textTransform: "none", fontWeight: 600, color: c.purpleBrand, "&:hover": {bgcolor: "rgba(98,88,232,0.06)"}}}>
              Send another request
            </Button>
          </Stack>
        ) : (
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{p: "clamp(32px, 4vw, 56px)", display: "flex", flexDirection: "column", gap: 1}}>
            <Typography sx={{fontSize: "11.5px", letterSpacing: "0.16em", textTransform: "uppercase", color: c.purpleDeep, fontWeight: 600, mb: 1.5}}>
              Book a free demo
            </Typography>
            <Stack direction={{xs: "column", sm: "row"}} gap={{xs: 0, sm: 1.5}}>
              <TextField label="Name" required fullWidth size="small" placeholder="Your full name" {...fieldProps("name", adorn(UserIcon))} />
              <TextField label="Phone" required fullWidth size="small" placeholder="+389 …" {...fieldProps("phone", adorn(PhoneIcon))} />
            </Stack>
            <TextField label="Work email" required fullWidth size="small" type="email" placeholder="you@company.com" {...fieldProps("email", adorn(Mail01Icon))} />
            <TextField label="Company (optional)" fullWidth size="small" placeholder="Your company name" {...fieldProps("company", adorn(Building01Icon))} />
            <TextField label="Monthly call volume" select fullWidth size="small" value={form.volume} onChange={set("volume")} helperText=" " sx={fieldSx}>
              {CALL_VOLUMES.map((v) => (
                <MenuItem key={v} value={v}>{v}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="What would you automate first?"
              required
              fullWidth
              multiline
              rows={4}
              placeholder="Refunds, order tracking, appointment booking…"
              {...fieldProps("message")}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={status === "sending"}
              endIcon={<HugeiconsIcon icon={ArrowRight01Icon} size={18} />}
              sx={{
                ...primaryButtonSx,
                mt: 1,
                minHeight: {xs: 52, md: 56},
                fontSize: 15,
                py: 1.75,
              }}
            >
              {status === "sending" ? "Sending…" : "Book a free demo"}
            </Button>

            {status === "error" && (
              <Typography sx={{fontSize: "12.5px", color: "#EF4444", lineHeight: 1.5, mt: 0.5}}>
                Something went wrong sending your request — please try again, or email us directly at{" "}
                <Typography component="a" href={`mailto:${CONTACT_EMAIL}`} sx={{color: "#EF4444", fontWeight: 600}}>
                  {CONTACT_EMAIL}
                </Typography>.
              </Typography>
            )}

            <Typography sx={{fontSize: "11.5px", color: c.inkMuted, lineHeight: 1.5, mt: 1.25}}>
              We reply within one business day. No newsletter, no reselling your data.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
